import type { LayerRenderer } from '../registry'

/**
 * Cadres (slot `frame`). ⚠️ Peints HORS du groupe clippé par
 * CaptainPortrait : leur encre vit autour du rayon 55.4 (au-delà du disque
 * r 53) et serait entièrement mangée par le clip. Rester dans le viewBox
 * 0-120. Ids de gradients TOUJOURS préfixés par `uid`.
 */
export const FRAMES: Record<string, LayerRenderer> = {
  frame_none: () => null,
  frame_corde: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-rope`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F0DCA8" />
          <stop offset="1" stopColor="#A5813F" />
        </linearGradient>
      </defs>
      <circle cx="60" cy="60" r="55.4" fill="none" stroke={`url(#${uid}-rope)`} strokeWidth="3.6" />
      <circle cx="60" cy="60" r="55.4" fill="none" stroke="#5A2A12" strokeWidth="3.6" strokeDasharray="3.2 4.6" opacity="0.55" />
    </>
  ),
  frame_bois_epave: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-bois`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#7A5636" />
          <stop offset="1" stopColor="#33220F" />
        </linearGradient>
      </defs>
      {/* anneau : base gradient + lisières int/ext (bois usé) */}
      <circle cx="60" cy="60" r="55.4" fill="none" stroke={`url(#${uid}-bois)`} strokeWidth="4" />
      <circle cx="60" cy="60" r="53.5" fill="none" stroke="#241608" strokeWidth="0.7" opacity="0.65" />
      <circle cx="60" cy="60" r="57.3" fill="none" stroke="#8A6540" strokeWidth="0.6" opacity="0.55" />
      {/* modelé : lumière haut-gauche + ombre bas-droit sur l'anneau */}
      <path d="M 6.5 45.7 A 55.4 55.4 0 0 1 45.7 6.5" fill="none" stroke="#9C7448" strokeWidth="2.4" strokeLinecap="round" opacity="0.55" />
      <path d="M 112.1 79 A 55.4 55.4 0 0 1 74.3 113.5" fill="none" stroke="#1C1006" strokeWidth="2.6" strokeLinecap="round" opacity="0.5" />
      {/* veinage : deux filets dash décalés (creux sombre / fibre claire) */}
      <circle cx="60" cy="60" r="56.3" fill="none" stroke="#2E1F12" strokeWidth="0.7" strokeDasharray="18 9" opacity="0.5" />
      <circle cx="60" cy="60" r="54.5" fill="none" stroke="#8A6540" strokeWidth="0.55" strokeDasharray="11 14" strokeDashoffset="6" opacity="0.5" />
      {/* nœuds du bois */}
      <ellipse cx="26.7" cy="104.2" rx="1.9" ry="1.1" fill="#241608" opacity="0.75" transform="rotate(53 26.7 104.2)" />
      <ellipse cx="101.2" cy="23" rx="1.6" ry="0.95" fill="#241608" opacity="0.75" transform="rotate(-42 101.2 23)" />
      {/* fissures / encoches radiales */}
      <path d="M 86.8 13.7 L 88.6 10.4" stroke="#2A1B0F" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M 9.7 78.3 L 6.2 79.6" stroke="#2A1B0F" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M 73.9 111.7 L 74.8 115.2" stroke="#2A1B0F" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M 13.7 33.3 L 10.4 31.4" stroke="#2A1B0F" strokeWidth="1.5" strokeLinecap="round" />
      {/* filet de lumière haut-gauche + rim light doré bord droit */}
      <path d="M 9.8 36.6 A 55.4 55.4 0 0 1 41.1 7.9" fill="none" stroke="#B98F5C" strokeWidth="1.1" opacity="0.75" />
      <path d="M 100 20 A 56.6 56.6 0 0 1 96.4 103.4" fill="none" stroke="#E5CC8F" strokeWidth="1.1" opacity="0.55" />
      {/* clous dorés : ombre portée + tête + éclat */}
      {([[112.1, 41.1], [7.8, 41.1], [79, 112.1]] as const).map(([x, y]) => (
        <g key={`${x}:${y}`}>
          <circle cx={x + 0.5} cy={y + 0.6} r="1.7" fill="#1C1006" opacity="0.6" />
          <circle cx={x} cy={y} r="1.7" fill="#C9A55C" />
          <circle cx={x - 0.5} cy={y - 0.5} r="0.6" fill="#F5EDD7" />
        </g>
      ))}
    </>
  ),
  frame_laiton: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-laiton`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#D9B36A" />
          <stop offset="1" stopColor="#8C6A32" />
        </linearGradient>
        <radialGradient id={`${uid}-rivet`} cx="0.35" cy="0.3" r="0.9">
          <stop offset="0" stopColor="#F7EFDE" />
          <stop offset="1" stopColor="#8C6A32" />
        </radialGradient>
      </defs>
      {/* double filet + gorge sombre tournée entre les deux */}
      <circle cx="60" cy="60" r="54.4" fill="none" stroke={`url(#${uid}-laiton)`} strokeWidth="2" />
      <circle cx="60" cy="60" r="56.6" fill="none" stroke={`url(#${uid}-laiton)`} strokeWidth="1.2" />
      <circle cx="60" cy="60" r="55.5" fill="none" stroke="#5C4318" strokeWidth="0.5" opacity="0.5" />
      {/* modelé métal : spéculaire haut-gauche sur les deux filets */}
      <path d="M 7.4 45.9 A 54.4 54.4 0 0 1 45.9 7.4" fill="none" stroke="#F5EDD7" strokeWidth="1.6" strokeLinecap="round" opacity="0.85" />
      <path d="M 6.8 40.6 A 56.6 56.6 0 0 1 40.6 6.8" fill="none" stroke="#F0DCA8" strokeWidth="0.8" strokeLinecap="round" opacity="0.7" />
      {/* ombre bas-droit sur les deux filets */}
      <path d="M 111.1 78.6 A 54.4 54.4 0 0 1 74.1 112.6" fill="none" stroke="#6B4E22" strokeWidth="2" strokeLinecap="round" opacity="0.55" />
      <path d="M 111.3 83.9 A 56.6 56.6 0 0 1 79.4 113.2" fill="none" stroke="#6B4E22" strokeWidth="1" strokeLinecap="round" opacity="0.5" />
      {/* reflet secondaire bas-gauche (métal poli) + rim light doré bord droit */}
      <path d="M 37 109.3 A 54.4 54.4 0 0 1 12.9 87.2" fill="none" stroke="#F0DCA8" strokeWidth="0.9" strokeLinecap="round" opacity="0.35" />
      <path d="M 109 31.7 A 56.6 56.6 0 0 1 109 88.3" fill="none" stroke="#E5CC8F" strokeWidth="1.1" strokeLinecap="round" opacity="0.7" />
      {/* rivets cardinaux : ombre portée + tête bombée + éclat */}
      {([[60, 4.5], [115.5, 60], [60, 115.5], [4.5, 60]] as const).map(([x, y]) => (
        <g key={`${x}:${y}`}>
          <circle cx={x + 0.6} cy={y + 0.7} r="2.1" fill="#5C4318" opacity="0.5" />
          <circle cx={x} cy={y} r="2.1" fill={`url(#${uid}-rivet)`} stroke="#6B4E22" strokeWidth="0.6" />
          <circle cx={x - 0.7} cy={y - 0.7} r="0.7" fill="#F7EFDE" />
        </g>
      ))}
    </>
  ),
  frame_argent_marine: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-argent`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#D8DDE2" />
          <stop offset="1" stopColor="#8A939E" />
        </linearGradient>
        <radialGradient id={`${uid}-medail`} cx="0.35" cy="0.3" r="0.95">
          <stop offset="0" stopColor="#4A5663" />
          <stop offset="1" stopColor="#2E3842" />
        </radialGradient>
      </defs>
      {/* anneau : base gradient + lisières int/ext */}
      <circle cx="60" cy="60" r="55.4" fill="none" stroke={`url(#${uid}-argent)`} strokeWidth="3.8" />
      <circle cx="60" cy="60" r="53.6" fill="none" stroke="#5A6470" strokeWidth="0.6" opacity="0.7" />
      <circle cx="60" cy="60" r="57.2" fill="none" stroke="#F0F4F7" strokeWidth="0.5" opacity="0.5" />
      {/* modelé : spéculaire haut-gauche + ombre bas-droit + reflet bas-gauche */}
      <path d="M 6.5 45.7 A 55.4 55.4 0 0 1 45.7 6.5" fill="none" stroke="#FDFEFF" strokeWidth="1.6" strokeLinecap="round" opacity="0.85" />
      <path d="M 112.1 79 A 55.4 55.4 0 0 1 74.3 113.5" fill="none" stroke="#4E5964" strokeWidth="2.2" strokeLinecap="round" opacity="0.55" />
      <path d="M 36.6 110.2 A 55.4 55.4 0 0 1 12 87.7" fill="none" stroke="#EAF0F5" strokeWidth="0.9" strokeLinecap="round" opacity="0.3" />
      {/* gravure de vague : double dash fin décalé (crête claire / creux sombre) */}
      <circle cx="60" cy="60" r="56.1" fill="none" stroke="#F0F4F7" strokeWidth="0.8" strokeDasharray="5 4.2" opacity="0.7" />
      <circle cx="60" cy="60" r="54.6" fill="none" stroke="#6E7883" strokeWidth="0.8" strokeDasharray="5 4.2" strokeDashoffset="4.6" opacity="0.55" />
      {/* rim light doré bord droit */}
      <path d="M 108 32.3 A 55.4 55.4 0 0 1 108 87.7" fill="none" stroke="#E5CC8F" strokeWidth="1.1" strokeLinecap="round" opacity="0.6" />
      {/* médaillon étoile de la Marine : ombre portée + disque modelé */}
      <circle cx="60.6" cy="111.8" r="5.6" fill="#232B34" opacity="0.5" />
      <circle cx="60" cy="111" r="5.6" fill={`url(#${uid}-medail)`} stroke="#8A939E" strokeWidth="0.8" />
      <path d="M 54.7 109.1 A 5.6 5.6 0 0 1 61.9 105.7" fill="none" stroke="#C6CFD8" strokeWidth="0.9" strokeLinecap="round" opacity="0.8" />
      <path d="M 64.3 107.4 A 5.6 5.6 0 0 1 64.3 114.6" fill="none" stroke="#E5CC8F" strokeWidth="0.8" strokeLinecap="round" opacity="0.6" />
      {/* étoile à 3 valeurs : base ivoire + facette droite ombrée + cerne */}
      <path d="M 60 106.4 L 61.1 109.5 L 64.4 109.6 L 61.8 111.6 L 62.7 114.7 L 60 112.9 L 57.3 114.7 L 58.2 111.6 L 55.6 109.6 L 58.9 109.5 Z" fill="#F5EDD7" stroke="#6E7883" strokeWidth="0.35" />
      <path d="M 60 106.4 L 61.1 109.5 L 64.4 109.6 L 61.8 111.6 L 62.7 114.7 L 60 112.9 Z" fill="#B9C2CC" opacity="0.55" />
      <circle cx="59.2" cy="110.2" r="0.55" fill="#FDFEFF" opacity="0.9" />
    </>
  ),
  frame_or_legende: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-or`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F0DCA8" />
          <stop offset="1" stopColor="#A5813F" />
        </linearGradient>
        <radialGradient id={`${uid}-perle`} cx="0.35" cy="0.3" r="0.9">
          <stop offset="0" stopColor="#F7E9C4" />
          <stop offset="1" stopColor="#A5813F" />
        </radialGradient>
      </defs>
      {/* lueur externe à deux voiles */}
      <circle cx="60" cy="60" r="57.8" fill="none" stroke="#E5CC8F" strokeWidth="1" opacity="0.35" />
      <circle cx="60" cy="60" r="59.4" fill="none" stroke="#E5CC8F" strokeWidth="0.6" opacity="0.15" />
      {/* double filet d'or massif + gorge sombre entre les deux */}
      <circle cx="60" cy="60" r="54" fill="none" stroke={`url(#${uid}-or)`} strokeWidth="3" />
      <circle cx="60" cy="60" r="57" fill="none" stroke={`url(#${uid}-or)`} strokeWidth="1.4" />
      <circle cx="60" cy="60" r="55.5" fill="none" stroke="#7A5E2C" strokeWidth="0.5" opacity="0.5" />
      {/* spéculaire haut-gauche sur les deux filets */}
      <path d="M 7.8 46 A 54 54 0 0 1 46 7.8" fill="none" stroke="#F7EFDE" strokeWidth="1.4" strokeLinecap="round" opacity="0.85" />
      <path d="M 4.9 45.3 A 57 57 0 0 1 45.3 4.9" fill="none" stroke="#F7EFDE" strokeWidth="0.7" strokeLinecap="round" opacity="0.7" />
      {/* ombre bas-droit sur les deux filets + reflet secondaire bas-gauche */}
      <path d="M 108.9 82.8 A 54 54 0 0 1 69.4 113.2" fill="none" stroke="#6B4E22" strokeWidth="2" strokeLinecap="round" opacity="0.55" />
      <path d="M 113.6 79.5 A 57 57 0 0 1 74.8 115.1" fill="none" stroke="#6B4E22" strokeWidth="0.9" strokeLinecap="round" opacity="0.45" />
      <path d="M 37.2 108.9 A 54 54 0 0 1 13.2 87" fill="none" stroke="#F0DCA8" strokeWidth="0.9" strokeLinecap="round" opacity="0.3" />
      {/* rim light bord droit */}
      <path d="M 108.3 29.8 A 57 57 0 0 1 108.3 90.2" fill="none" stroke="#E5CC8F" strokeWidth="1.2" strokeLinecap="round" opacity="0.75" />
      {/* 8 perles d'or (tous les 45°) : ombre portée + bombé + point de lumière */}
      {([[115.5, 60], [99.2, 99.2], [60, 115.5], [20.8, 99.2], [4.5, 60], [20.8, 20.8], [60, 4.5], [99.2, 20.8]] as const).map(([x, y]) => (
        <g key={`${x}:${y}`}>
          <circle cx={x + 0.6} cy={y + 0.7} r="2.3" fill="#7A5E2C" opacity="0.5" />
          <circle cx={x} cy={y} r="2.3" fill={`url(#${uid}-perle)`} stroke="#8A6A33" strokeWidth="0.5" />
          <circle cx={x - 0.8} cy={y - 0.8} r="0.7" fill="#FDF7E3" />
        </g>
      ))}
    </>
  ),
  frame_fanal_cuivre: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-cuivre`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#EDAE72" />
          <stop offset="1" stopColor="#8A4419" />
        </linearGradient>
        <radialGradient id={`${uid}-fanalVerre`} cx="0.35" cy="0.28" r="0.95">
          <stop offset="0" stopColor="#FBEEC2" />
          <stop offset="1" stopColor="#C2761F" />
        </radialGradient>
      </defs>
      {/* anneau de cuivre : base gradient + lisières int/ext */}
      <circle cx="60" cy="60" r="55.4" fill="none" stroke={`url(#${uid}-cuivre)`} strokeWidth="3.8" />
      <circle cx="60" cy="60" r="53.6" fill="none" stroke="#5E2E12" strokeWidth="0.6" opacity="0.7" />
      <circle cx="60" cy="60" r="57.2" fill="none" stroke="#F2C495" strokeWidth="0.5" opacity="0.5" />
      {/* modelé : spéculaire haut-gauche, ombre bas-droit, reflet bas-gauche */}
      <path d="M 6.5 45.7 A 55.4 55.4 0 0 1 45.7 6.5" fill="none" stroke="#FBE0C4" strokeWidth="1.6" strokeLinecap="round" opacity="0.8" />
      <path d="M 112.1 79 A 55.4 55.4 0 0 1 74.3 113.5" fill="none" stroke="#4A2109" strokeWidth="2.2" strokeLinecap="round" opacity="0.55" />
      <path d="M 36.6 110.2 A 55.4 55.4 0 0 1 12 87.7" fill="none" stroke="#F2C495" strokeWidth="0.9" strokeLinecap="round" opacity="0.3" />
      {/* vert-de-gris : deux voiles de patine irréguliers */}
      <circle cx="60" cy="60" r="56.2" fill="none" stroke="#4E8C7A" strokeWidth="1.2" strokeDasharray="7 21" opacity="0.4" />
      <circle cx="60" cy="60" r="54.5" fill="none" stroke="#3E7566" strokeWidth="0.9" strokeDasharray="4 27" strokeDashoffset="13" opacity="0.35" />
      {/* rim light doré bord droit */}
      <path d="M 108 32.3 A 55.4 55.4 0 0 1 108 87.7" fill="none" stroke="#E5CC8F" strokeWidth="1.1" strokeLinecap="round" opacity="0.65" />
      {/* fanal allumé en pied : halo, anse, cage, verre, flamme */}
      <circle cx="60" cy="111.4" r="7.6" fill="#E5A55C" opacity="0.14" />
      <path d="M 58.2 104.4 A 1.9 1.9 0 0 1 61.8 104.4 L 61.8 106.6 L 60.7 106.6 A 0.75 0.75 0 0 0 59.3 106.6 L 58.2 106.6 Z" fill="#8A4419" />
      <path d="M 55.6 106.6 L 64.4 106.6 L 63.9 108.2 L 56.1 108.2 Z" fill={`url(#${uid}-cuivre)`} />
      <path d="M 56.9 108.2 L 63.1 108.2 L 62.5 116.4 L 57.5 116.4 Z" fill={`url(#${uid}-fanalVerre)`} />
      <path d="M 60 109.6 C 61.6 111.6 62 113.4 61.1 114.6 C 60.6 115.2 59.4 115.2 58.9 114.6 C 58 113.4 58.4 111.6 60 109.6 Z" fill="#FBEEC2" />
      <path d="M 60 111.6 C 60.8 112.7 61 113.7 60.5 114.4 C 60.2 114.7 59.8 114.7 59.5 114.4 C 59 113.7 59.2 112.7 60 111.6 Z" fill="#FFFBEE" />
      <path d="M 55.6 116.4 L 64.4 116.4 L 64.4 118 L 55.6 118 Z" fill="#8A4419" />
      <path d="M 57.6 108.4 L 57.1 116.2 M 62.4 108.4 L 62.9 116.2" fill="none" stroke="#8A4419" strokeWidth="0.8" strokeLinecap="round" />
      <path d="M 57.4 108.8 L 57 115.8" fill="none" stroke="#F2C495" strokeWidth="0.4" strokeLinecap="round" opacity="0.7" />
      <path d="M 56 107 L 64 107" fill="none" stroke="#FBE0C4" strokeWidth="0.6" strokeLinecap="round" opacity="0.7" />
    </>
  ),
  frame_fanal_chene: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-chene`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#B58551" />
          <stop offset="1" stopColor="#4A2F16" />
        </linearGradient>
        <linearGradient id={`${uid}-cheneFer`} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0" stopColor="#59636E" />
          <stop offset="1" stopColor="#242B33" />
        </linearGradient>
      </defs>
      {/* anneau de douves : base gradient + lisières int/ext */}
      <circle cx="60" cy="60" r="55.4" fill="none" stroke={`url(#${uid}-chene)`} strokeWidth="4.4" />
      <circle cx="60" cy="60" r="53.3" fill="none" stroke="#2E1C0A" strokeWidth="0.7" opacity="0.7" />
      <circle cx="60" cy="60" r="57.5" fill="none" stroke="#C39763" strokeWidth="0.6" opacity="0.5" />
      {/* modelé : lumière haut-gauche + ombre bas-droit */}
      <path d="M 6.5 45.7 A 55.4 55.4 0 0 1 45.7 6.5" fill="none" stroke="#CFA46E" strokeWidth="2.2" strokeLinecap="round" opacity="0.6" />
      <path d="M 112.1 79 A 55.4 55.4 0 0 1 74.3 113.5" fill="none" stroke="#241408" strokeWidth="2.6" strokeLinecap="round" opacity="0.5" />
      {/* fibre du chêne : filet clair + filet sombre décalés */}
      <circle cx="60" cy="60" r="56.4" fill="none" stroke="#C39763" strokeWidth="0.55" strokeDasharray="14 10" opacity="0.45" />
      <circle cx="60" cy="60" r="54.3" fill="none" stroke="#33200C" strokeWidth="0.6" strokeDasharray="9 16" strokeDashoffset="7" opacity="0.5" />
      {/* joints de douves : chaque planche est bornée par une entaille */}
      <g stroke="#2E1C0A" strokeWidth="1.1" strokeLinecap="round" opacity="0.75">
        <path d="M 109.8 41.9 L 114.3 40.2" />
        <path d="M 82.4 12.0 L 84.4 7.6" />
        <path d="M 41.9 10.2 L 40.2 5.7" />
        <path d="M 12.0 37.6 L 7.6 35.6" />
        <path d="M 10.2 78.1 L 5.7 79.8" />
        <path d="M 37.6 108.0 L 35.6 112.4" />
        <path d="M 78.1 109.8 L 79.8 114.3" />
        <path d="M 108.0 82.4 L 112.4 84.4" />
      </g>
      {/* rim light doré bord droit */}
      <path d="M 108 32.3 A 55.4 55.4 0 0 1 108 87.7" fill="none" stroke="#E5CC8F" strokeWidth="1.1" strokeLinecap="round" opacity="0.55" />
      {/* quatre feuillards de fer en diagonale, rivetés */}
      {([[99.2, 20.8, -45], [20.8, 20.8, -135], [20.8, 99.2, -225], [99.2, 99.2, -315]] as const).map(([x, y, rot]) => (
        <g key={`${x}:${y}`} transform={`rotate(${rot} ${x} ${y})`}>
          <rect x={x - 3.6} y={y - 7} width="7.2" height="14" rx="1.4" fill={`url(#${uid}-cheneFer)`} stroke="#171C22" strokeWidth="0.6" />
          <path d={`M ${x - 2.6} ${y - 6} L ${x - 2.6} ${y + 6}`} fill="none" stroke="#7A848F" strokeWidth="0.7" strokeLinecap="round" opacity="0.7" />
          <circle cx={x} cy={y - 4.2} r="1.1" fill="#8A939E" stroke="#171C22" strokeWidth="0.4" />
          <circle cx={x} cy={y + 4.2} r="1.1" fill="#8A939E" stroke="#171C22" strokeWidth="0.4" />
          <circle cx={x - 0.4} cy={y - 4.6} r="0.35" fill="#E2E8ED" />
          <circle cx={x - 0.4} cy={y + 3.8} r="0.35" fill="#E2E8ED" />
        </g>
      ))}
      {/* plaque de fer en pied, flamme du fanal gravée à l'or */}
      <rect x="52.4" y="104.4" width="15.2" height="13.4" rx="2" fill={`url(#${uid}-cheneFer)`} stroke="#171C22" strokeWidth="0.7" />
      <path d="M 53.8 105.6 L 53.8 116.6" fill="none" stroke="#7A848F" strokeWidth="0.8" strokeLinecap="round" opacity="0.65" />
      <path d="M 60.2 105.6 C 60.6 108.4 62.2 109.8 63 111.6 C 64.2 114.2 62.6 116.6 60 116.6 C 57.4 116.6 55.8 114.2 57 111.6 C 57.6 110.2 58.6 109.4 59 108 C 59.3 108.8 59.7 109.3 60.2 109.6 C 60.4 108.4 60.3 107 60.2 105.6 Z" fill="#C9A55C" />
      <path d="M 60 110.4 C 60.6 111.8 61.6 112.6 61.9 113.8 C 62.3 115.2 61.3 116.1 60 116.1 C 58.7 116.1 57.7 115.2 58.1 113.8 C 58.4 112.7 59.4 111.8 60 110.4 Z" fill="#F0DCA8" />
      <circle cx="59.4" cy="114.2" r="0.6" fill="#FDF7E3" />
      <circle cx="54" cy="106.4" r="0.9" fill="#8A939E" stroke="#171C22" strokeWidth="0.35" />
      <circle cx="66" cy="106.4" r="0.9" fill="#8A939E" stroke="#171C22" strokeWidth="0.35" />
      <circle cx="54" cy="115.8" r="0.9" fill="#8A939E" stroke="#171C22" strokeWidth="0.35" />
      <circle cx="66" cy="115.8" r="0.9" fill="#8A939E" stroke="#171C22" strokeWidth="0.35" />
    </>
  ),
}
